import type { Board } from "../board";
import type { Direction } from "../direction";
import { MovementEffect, STANDARD_MOVEMENT_TIME } from "../effects/movementEffect";
import type { ParticleEmitter, SomethingWithState } from "../interfaces";
import { ParticleType } from "../particleType";
import type { PhysicalThingWithHp } from "../physicalThingWithHp";
import type { PhysicalThing } from "../physicalThing";
import { Player } from "../player";
import { randomChance } from "../random";
import { deserializeThing, deserializeXY, serializeXY } from "../serialization";
import { SimpleProjectile } from "../simpleProjectile";
import { Chasm } from "../things/chasm";
import { DestroyedConcreteBlock } from "../things/destroyedConcreteBlock";
import { DustEmitter } from "../things/dustEmitter";
import { ConcreteFloor } from "../things/floors/concreteFloor";
import { Floor } from "../things/floors/floor";
import { GraySlab } from "../things/floors/graySlab";
import { GrayStoneFloor1 } from "../things/floors/grayStoneFloor1";
import { GrayStoneFloor2 } from "../things/floors/grayStoneFloor2";
import { TornGround } from "../things/floors/tornGround";
import { WarningFloor } from "../things/floors/warningFloor";
import { LavaPool } from "../things/lavaPool";
import { LevelChangerThing } from "../things/levelChangerThing";
import { MultiTileThingCore } from "../things/multitile/multiTileThingCore";
import { MultiTileThingPart } from "../things/multitile/multiTileThingPart";
import { WaterTile } from "../things/waterTile";
import type { XY } from "../xy";

const TURNS_UNTIL_GROWTH = 1;
const SUCTION_EFFECT_TIME = STANDARD_MOVEMENT_TIME * 1;
const FLOOR_SUCTION_EXTRA_RADIUS = 1;
// Per sweep — a side-row cell is swept ~10 times while in the outer ring, so the
// cumulative tear chance is ~1-(1-p)^10 (0.07 => ~50%).
const OUTER_FLOOR_SUCTION_PROBABILITY = 0.07;

export type BlackHoleSize = "small" | "big";

export class BlackHoleProjectile extends SimpleProjectile implements SomethingWithState, ParticleEmitter {
	static key = "BlackHoleProjectile";

	getParticleType(): ParticleType {
		return this.size === "big" ? ParticleType.BLACK_HOLE_SUCTION_BIG : ParticleType.BLACK_HOLE_SUCTION;
	}

	private size: BlackHoleSize = "small";
	private turnsUntilGrowth: number = TURNS_UNTIL_GROWTH;

	constructor(xy: XY, direction: Direction) {
		super(xy, direction);
	}

	getState(): BlackHoleSize {
		return this.size;
	}

	/** True if xy is inside this hole's current footprint (1x1 small, 3x3 big). */
	covers(xy: XY): boolean {
		return xy.isInSquareRadius(this.xy, this.consumeRadius());
	}

	/** True if any live black hole's footprint claims xy — beams, flames and jumps stop there. */
	static blocksCell(board: Board, xy: XY): boolean {
		return board
			.getPhysicalThings()
			.some((t) => t instanceof BlackHoleProjectile && t.active && t.covers(xy));
	}

	/** True if xy lies in the 3-wide corridor this hole will sweep ahead of itself. */
	shadows(xy: XY): boolean {
		for (let cell = this.xy; this.board!.isInBounds(cell); cell = cell.addDirection(this.direction)) {
			if (xy.isInSquareRadius(cell, 1)) return true;
		}
		return false;
	}

	processTurn(board: Board): void {
		if (this.active && board.currentProcessingPass === 0 && this.size === "small") {
			this.turnsUntilGrowth--;
			if (this.turnsUntilGrowth <= 0) this.size = "big";
		}
		super.processTurn(board);
	}

	protected override moveTo(destination: XY): boolean {
		const previousFootprint = this.getFootprint(this.consumeRadius());
		const moved = super.moveTo(destination);
		if (moved) {
			for (const cell of previousFootprint) {
				if (!cell.isInSquareRadius(this.xy, this.consumeRadius())) {
					this.board!.addThing(new DustEmitter(cell));
				}
			}
		} else if (!this.active) {
			// Exited the board — dust the final footprint so the trail doesn't cut off
			for (const cell of previousFootprint) {
				this.board!.addThing(new DustEmitter(cell));
			}
		}
		return moved;
	}

	override checkForTarget(): boolean {
		if (!this.active) return false;
		let consumedAny = false;
		for (const cellXY of this.getFootprint(this.consumeRadius())) {
			for (const thing of [...this.board!.getCell(cellXY).getThings()]) {
				if (!thing.active || !this.canConsume(thing)) continue;
				this.consume(thing);
				consumedAny = true;
			}
		}
		if (this.size === "big") {
			for (const cellXY of this.getFootprint(this.consumeRadius() + FLOOR_SUCTION_EXTRA_RADIUS)) {
				const isOuterRing = !cellXY.isInSquareRadius(this.xy, this.consumeRadius());
				if (isOuterRing && !randomChance(OUTER_FLOOR_SUCTION_PROBABILITY)) continue;
				this.suckFloor(cellXY);
			}
		}
		return consumedAny;
	}

	private suckFloor(cellXY: XY): void {
		const things = this.board!.getThingsAt(cellXY);
		if (things.some((t) => t instanceof WaterTile || t instanceof Chasm || t instanceof LavaPool)) return;
		if (things.some((t) => t instanceof TornGround || t instanceof DestroyedConcreteBlock)) return;
		let suckedFloor = things.find((t): t is Floor => t instanceof Floor && t.active) ?? null;
		if (suckedFloor) {
			this.addSuctionEffect(suckedFloor);
			this.board!.removeThing(suckedFloor);
		} else if (this.board!.defaultFloor) {
			suckedFloor = deserializeThing({
				type: this.board!.defaultFloor,
				xy: serializeXY(cellXY),
				visualId: this.board!.getTerrainVisualId(cellXY),
			}) as Floor;
			this.addSuctionEffect(suckedFloor);
		}
		const remnant =
			suckedFloor && this.leavesConcreteRubble(suckedFloor)
				? new DestroyedConcreteBlock(cellXY)
				: new TornGround(cellXY);
		this.board!.addThing(remnant);
	}

	private leavesConcreteRubble(floor: Floor): boolean {
		return (
			floor instanceof GraySlab ||
			floor instanceof GrayStoneFloor1 ||
			floor instanceof GrayStoneFloor2 ||
			floor instanceof WarningFloor ||
			floor instanceof ConcreteFloor
		);
	}

	private consumeRadius(): number {
		return this.size === "big" ? 1 : 0;
	}

	private getFootprint(radius: number): XY[] {
		return this.xy.getAreaInSquareRadius(radius).filter((xy) => this.board!.isInBounds(xy));
	}

	private canConsume(thing: PhysicalThing): boolean {
		if (thing === this) return false;
		if (!thing.isTangible()) return false;
		return !(
			thing instanceof Floor ||
			thing instanceof WaterTile ||
			thing instanceof Chasm ||
			thing instanceof LavaPool ||
			thing instanceof DestroyedConcreteBlock ||
			thing instanceof LevelChangerThing
		);
	}

	private consume(thing: PhysicalThing): void {
		if (thing instanceof Player) {
			thing.kill(null, this);
			return;
		}
		if (thing instanceof MultiTileThingPart) {
			this.consumeMultiTile(thing.controller.getThing());
			return;
		}
		if (thing instanceof MultiTileThingCore) {
			this.consumeMultiTile(thing);
			return;
		}
		this.addSuctionEffect(thing);
		this.board!.removeThing(thing);
	}

	private consumeMultiTile(core: MultiTileThingCore): void {
		if (!core.active) return;
		this.addSuctionEffect(core);
		if (core.controller) {
			for (const part of core.controller.getParts()) {
				this.board!.removeThing(part);
			}
		}
		this.board!.removeThing(core);
	}

	private addSuctionEffect(thing: PhysicalThing): void {
		if (thing.xy.equals(this.xy)) return;
		this.board!.addEffect(new MovementEffect(thing, thing.xy, this.xy, SUCTION_EFFECT_TIME));
	}

	protected damageTarget(_target: PhysicalThingWithHp): number {
		return 0;
	}

	onHit(_damageDealt: number): void {}

	serialize(): Record<string, unknown> {
		return { ...super.serialize(), size: this.size, turnsUntilGrowth: this.turnsUntilGrowth };
	}

	static deserialize(data: any): BlackHoleProjectile {
		const projectile = new BlackHoleProjectile(deserializeXY(data.xy), data.direction as Direction);
		projectile.visualId = data.visualId;
		projectile.size = data.size;
		projectile.turnsUntilGrowth = data.turnsUntilGrowth;
		return projectile;
	}
}
